import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TrophyIcon, CheckCircleIcon, StarIcon } from '@heroicons/react/24/outline';

const ProgressHistory = ({ gymnastId }) => {
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await axios.get(`/api/progress/gymnast/${gymnastId}/history`);
        setHistoryData(response.data);
      } catch (error) {
        setError('Failed to load progress history');
      } finally {
        setLoading(false);
      }
    };

    if (gymnastId) {
      fetchHistory();
    }
  }, [gymnastId]);

  const shortDate = (dateString) => new Date(dateString)
    .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const dayMonth = (dateString) => new Date(dateString)
    .toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

  const formatMonth = (monthString) => {
    const [year, month] = monthString.split('-');
    return new Date(year, month - 1).toLocaleDateString('en-GB', { year: 'numeric', month: 'long' });
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        {error}
      </div>
    );
  }

  if (!historyData || historyData.history.length === 0) {
    return (
      <div className="card ph">
        <h4 className="ph__title">Progress history</h4>
        <p className="ph__muted">Skills and levels will appear here once a coach marks them as completed.</p>
      </div>
    );
  }

  const { summary } = historyData;
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

  return (
    <div className="card ph">
      <h4 className="ph__title">Progress history</h4>
      <p className="ph__summary">
        <strong>{summary.totalSkillsCompleted}</strong> skills · <strong>{summary.totalLevelsCompleted}</strong> levels
        {summary.totalAwards > 0 && <> · <strong>{summary.totalAwards}</strong> {summary.totalAwards === 1 ? 'award' : 'awards'}</>}{' '}
        · <strong>{summary.totalDaysActive}</strong> days active
      </p>
      {summary.firstCompletion && (
        <p className="ph__muted">
          Started {shortDate(summary.firstCompletion)}
          {summary.latestCompletion && summary.latestCompletion !== summary.firstCompletion && ` · Latest ${shortDate(summary.latestCompletion)}`}
        </p>
      )}

      {historyData.timeline.map(monthData => {
        // Levels, awards and skills together, newest first.
        const awards = monthData.awards || [];
        const items = [
          ...monthData.levels.map(l => ({ ...l, type: 'level' })),
          ...awards.map(a => ({ ...a, type: 'award' })),
          ...monthData.skills.map(sk => ({ ...sk, type: 'skill' })),
        ].sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));
        let lastCoach = null;
        return (
          <section key={monthData.month} className="ph__month">
            <h5 className="ph__month-title">
              {formatMonth(monthData.month)}
              <span className="ph__muted">
                {monthData.skills.length > 0 && ` · ${plural(monthData.skills.length, 'skill')}`}
                {monthData.levels.length > 0 && ` · ${plural(monthData.levels.length, 'level')}`}
                {awards.length > 0 && ` · ${plural(awards.length, 'award')}`}
              </span>
            </h5>
            <ul className="ph__list">
              {items.map(item => {
                const coach = `${item.user.firstName} ${item.user.lastName}`;
                const showCoach = coach !== lastCoach;
                lastCoach = coach;
                const isLevel = item.type === 'level';
                const isAward = item.type === 'award';
                const routine = isLevel && item.routineInfo
                  ? `${item.routineInfo.name || `Routine ${item.routineInfo.order}`}${item.routineInfo.isAlternative ? ' (alternative)' : ''}`
                  : '';
                return (
                  <li key={`${item.type}-${item.id}`} className={`ph__item${isLevel || isAward ? ' ph__item--level' : ''}`}>
                    {isLevel && <TrophyIcon className="ph__icon" style={{ color: '#b7791f' }} aria-hidden="true" />}
                    {isAward && <StarIcon className="ph__icon" style={{ color: '#b7791f' }} aria-hidden="true" />}
                    {!isLevel && !isAward && <CheckCircleIcon className="ph__icon" style={{ color: '#27ae60' }} aria-hidden="true" />}
                    <div className="ph__body">
                      <div className="ph__name">
                        {isLevel ? `Level ${item.levelInfo.number} completed: ${item.levelInfo.name}`
                          : isAward ? `Awarded: ${item.itemName}` : item.itemName}
                      </div>
                      <div className="ph__meta">
                        {isAward && <span>Certificate</span>}
                        {!isLevel && !isAward && (item.levelInfo
                          ? <span>Level {item.levelInfo.number}: {item.levelInfo.name}</span>
                          : <span>No longer part of a level</span>)}
                        {routine && <span>{routine}</span>}
                        <span>{dayMonth(item.completedAt)}</span>
                        {showCoach && <span>by {coach}</span>}
                      </div>
                      {item.notes && <div className="ph__notes">"{item.notes}"</div>}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

export default ProgressHistory;
